import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import { useHistory } from 'react-router-dom';
import { makeStyles, Theme } from '@material-ui/core/styles';
import BackIcon from '../icons/BackIcon';
import LinkIcon from '../icons/LinkIcon';
import PinIcon from '../icons/PinIcon';
import PDFIcon from '../icons/PDFIcon';
import CompareIcon from '../icons/CompareIcon';
import SaveIcon from '../icons/SaveIcon';
import AlertIcon from '../icons/AlertIcon';
import RemoveIcon from '../icons/RemoveIcon';
import FreshmanIcon from '../icons/FreshmanIcon';
import TFPillLogoColorIcon from '../icons/TFPillLogoColorIcon';
import On3LogoIcon from '../icons/On3LogoIcon';
import Score, { SCORE_TYPE } from '../atoms/Score';
import Loader from '../atoms/Loader';
import Action from '../atoms/Action';
import Avatar from '../atoms/Avatar';
import CFCLogo from '../atoms/CFCLogo';
import Tooltip from '../atoms/Tooltip';
import { ToastType } from '../atoms/Toast';
import EditButton from '../atoms/EditButton';
import TeamAutoComplete from '../molecules/TeamAutoComplete';
import MultiSportList from '../molecules/MultiSportList';
import ConfirmDialog from './ConfirmDialog';
import { PlayerPDFQueryParameter, TEAM_TYPE } from '../templates/PlayerPageTemplate';
import gql from '../services/gql';
import { getPrimaryHighSchool } from '../services/player';
import exportPDFReport, { PDFExportPage } from '../services/export-pdf-report';
import { FONT_PROXIMA_NOVA } from '../styles/fonts';
import {
  COLOR_BACKGROUND_LIGHT,
  COLOR_TEXT,
  COLOR_DARK_GRAY,
  COLOR_ORANGE,
  COLOR_BACKGROUND_WARM,
  COLOR_MIDDLE_GRAY,
  COLOR_WHITE,
  COLOR_BLUE,
  COLOR_SHADOW,
  COLOR_GREEN,
  COLOR_PURPLE,
} from '../styles/colors';
import MEDIA from '../styles/media';
import Player from '../types/Player';
import Team from '../types/Team';
import User from '../types/User';
import Position from '../types/Position';
import CircleBadge from '../atoms/CircleBadge';
import SavedPlayer from '../types/SavedPlayer';
import LabledScore from '../atoms/LabledScore';
import Toggle from '../atoms/Toggle';
import IconButton from '../atoms/IconButton';
import { RemoveCircleOutlineOutlined } from '@material-ui/icons';

export interface UpdatedPlayerProfileHeaderProps {
  player?: Player;
  loading: boolean;
  divITeams: Team[];
  nflTeams: Team[];
  selectedTeamType: string;
  selectedCollegeTeam?: Team;
  selectedNFLTeam?: Team;
  positions?: Position[];
  selectedPosition?: Position;
  setSelectedPosition: React.Dispatch<React.SetStateAction<Position | undefined>>;
  user?: User;
  onCollegeTeamSelected: (team: Team | null) => void;
  onNFLTeamSelected: (team: Team | null) => void;
  setLoading: (isLoading: boolean) => void;
  showToast: (message: any, type: ToastType) => void;
  onEditPlayer: () => void,
  isNFL?: boolean;
  isAdmin?: boolean;
  IS_ADMIN_OR_NFL_SCOUT?: boolean;
}

const useStyles = makeStyles((theme: Theme) => ({
  header: {
    display: 'flex',
    alignItems: 'center',
    background: COLOR_BACKGROUND_LIGHT,
    minHeight: '240px',
    padding: theme.spacing(3, 5),
    position: 'relative',

    [theme.breakpoints.down('xs')]: {
      minHeight: 'unset',
      padding: theme.spacing(3),
    },
  },
  headerContent: {
    flexGrow: 1,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(5),

    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
    },
  },
  playerIntro: {
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    width: '200px',
  },
  avatarContainer: {
    position: 'relative',
    width: 'fit-content',
    margin: '0 auto',
  },
  avatar: {
    width: '128px',
    height: '128px',
    margin: '0 auto',
    border: `1px solid ${COLOR_MIDDLE_GRAY}`,

    [theme.breakpoints.down('xs')]: {
      width: '100px',
      height: '100px',
    },
  },
  playerIntroContent: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    textAlign: 'center',
  },

  editAction: {
      position: 'absolute',
      color: COLOR_BLUE,
      bottom: '0px',
      right: '-4px',
      background: COLOR_WHITE,
      zIndex: 10,
      borderRadius: '50%',
      height: '40px',
      width: '40px',
      boxShadow: `0 10px 10px 0 ${COLOR_SHADOW}`,
      border: `1px solid ${COLOR_SHADOW}`,
      cursor: 'pointer',
    },
    
  playerName: {
    margin: theme.spacing(1, 0, 0, 0),
    fontSize: theme.typography.pxToRem(28),
    lineHeight: 1.1,
    color: COLOR_TEXT,

    [theme.breakpoints.down('xs')]: {
      fontSize: theme.typography.pxToRem(22),
    },
  },
  playerOverview: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),

    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
  },
  playerOverviewRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(3, 5),

    [theme.breakpoints.down('sm')]: {
      justifyContent: 'center',
    },

    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
      alignItems: 'center',
    },
  },
  scoreAndMultiSportRow: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(10),
    marginRight: 'auto',

    [theme.breakpoints.down('md')]: {
      gap: theme.spacing(5),
    },

    [theme.breakpoints.down('sm')]: {
      justifyContent: 'center',
      marginRight: 0,
    },
  },
  cfcLogo: {
    width: '30px',
    margin: theme.spacing(1.5, 1.5, 0, 0),
  },
  playerScoreRow: {
    display: 'flex',
    gap: theme.spacing(3),
  },
  playerScoreWrapper: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column',
    flexShrink: 0,
    justifyContent: 'space-between',
    maxWidth: '150px',

    '&:first-of-type': {
      marginLeft: 0,
    },
  },
  playerScore: {
  },
  playerMultiSColumn: {
    display: 'flex',
  },
  playerMultiSSection: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: theme.spacing(0, 4),
    borderRight: `2px solid ${COLOR_MIDDLE_GRAY}`,

    '&:first-child': {
      paddingLeft: 0,
    },

    '&:last-child': {
      paddingRight: 0,
      borderRight: 0,
    },
  },
  multiSportSpecialistChip: {
    backgroundColor: COLOR_DARK_GRAY,
    borderRadius: '999px',
    color: COLOR_WHITE,
    fontFamily: FONT_PROXIMA_NOVA,
    fontSize: theme.typography.pxToRem(12),
    fontWeight: 700,
    lineHeight: 1,
    padding: theme.spacing(1, 2.5),
    marginBottom: theme.spacing(1),
  },
  multiSportCount: {
    fontSize: theme.typography.pxToRem(22),
    fontWeight: 700,
    color: COLOR_TEXT,
  },
  multiSportLabel: {
    flexGrow: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    textAlign: 'center',
    whiteSpace: 'nowrap',
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 700,
    textTransform: 'uppercase',
  },
  multiSportIcon: {
    display: 'flex',
    justifyContent: 'center',
    width: '100%',

    '& svg': {
      width: 'auto',
      maxWidth: '44px',
      height: 'auto',
      maxHeight: '26px',
    },
  },
  multiSportTooltipList: {
    gap: theme.spacing(1),
    alignItems: 'center',
  },
  multiSportTooltipIcon: {
    margin: 0,
    color: COLOR_WHITE,
  },
  pdLeft: {
    paddingLeft: '18px'
  },
  leftAlign: {
    display: 'flex',
    alignItems: 'flex-start',
  },
  noScoreText: {
    fontSize: theme.typography.pxToRem(18),
    fontWeight: 700,
  },
  freshmanIcon: {
    cursor: 'help',
    height: 48,
    width: 48,
  },
  paiFreshmanIcon: {
    color: COLOR_ORANGE,
  },
  combineFreshmanIcon: {
    color: COLOR_GREEN,
  },
  checkIconClass: {
    height: 30,
    width: 30,
  },
  proCombineWrapper: {
  },
  teamDropDownWrapper: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(1, 2),

    [theme.breakpoints.down('sm')]: {
      justifyContent: 'center',
      width: '100%',
    },
  },
  teamDropDownLabel: {
    fontSize: theme.typography.pxToRem(16),
    fontWeight: 700,
    color: COLOR_TEXT,
  },
  teamSelect: {
    minWidth: '220px',

    '& input': {
      boxSizing: 'border-box',
      height: '70px',
    },

    [theme.breakpoints.down('xs')]: {
      minWidth: 0,
      width: '100%',
      maxWidth: 320,
    },
  },
  withNFLTeamSelect: {
    width: '224px',
    minWidth: '224px',
  },
  switch: {
    flexShrink: 0,
  },
  toolbarRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(5),

    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
    },
  },
  actions: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
  },
  action: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '50px',
    height: '50px',
    padding: theme.spacing(.5),
    borderRadius: '50%',
    border: `2px solid ${COLOR_MIDDLE_GRAY}`,
    color: COLOR_DARK_GRAY,
    background: COLOR_BACKGROUND_WARM,
    fontSize: theme.typography.pxToRem(16),
    transition: 'background 0.3s',

    '&:hover': {
      background: COLOR_BACKGROUND_LIGHT,
      textDecoration: 'none',
      borderColor: COLOR_BLUE,

      '& $actionIcon': {
        color: COLOR_BLUE,
      },

      '& $actionText': {
        color: COLOR_TEXT,
      },
    },
  },
  actionIcon: {
    width: '22px',
    height: '22px',
  },
  actionText: {
    color: COLOR_TEXT,
  },
}), { name: UpdatedPlayerProfileHeader.name });

export default function UpdatedPlayerProfileHeader(props: UpdatedPlayerProfileHeaderProps) {
  const {
    player,
    loading,
    divITeams,
    nflTeams,
    selectedTeamType,
    selectedCollegeTeam,
    selectedNFLTeam,
    positions,
    selectedPosition,
    setSelectedPosition,
    onCollegeTeamSelected,
    onNFLTeamSelected,
    setLoading,
    isNFL,
    isAdmin,
    user,
    showToast,
    onEditPlayer,
    IS_ADMIN_OR_NFL_SCOUT = false,
  } = props;
  const classes = useStyles();
  const history = useHistory();
  const [isPinned, setIsPinned] = useState<boolean>(false);
  const [savedPlayer, setSavedPlayer] = useState<SavedPlayer | null>(null);
  const [isArchived, setIsArchived] = useState<boolean>(false);
  const [hasAlert, setHasAlert] = useState<boolean>(false);

  const [archivePlayerDialogOpen, setArchivePlayerDialogOpen] = useState<boolean>(false);
  const [removePlayerDialogOpen, setRemovePlayerDialogOpen] = useState<boolean>(false);

  useEffect(() => {
    if (player) {
      setLoading(true);

      if (player.isArchived) {
        setIsArchived(true);
      }

      Promise.allSettled([
        checkIsPinned(player.id),
        checkIsSavedPlayer(player.id),
        checkPlayerHasAlert(player.id),
      ])
        .finally(() => setLoading(false));
    }
  }, [player]);
  const playerHasColleges = player && player.playerColleges && player.playerColleges.length > 0;
  const playerHasNFLTeams = player && player.playerNFLTeams && player.playerNFLTeams.length > 0;
  const playerHasHighSchools = player && player.highSchools && player.highSchools.length > 0;
  const playerIsHS = playerHasHighSchools && !playerHasColleges && !playerHasNFLTeams;

  function checkIsPinned(id: number) {
    return gql(`
        isPinned (playerId: ${id})
      `)
      .then((data: any) => data && data.isPinned as boolean)
      .then((pinnedStatus: boolean) => setIsPinned(pinnedStatus));
  }

  function checkIsSavedPlayer(id: number) {
    return gql(`
        getSavedPlayer (playerId: ${id}) {
          id
          playerId
          folderId
          player {
            id
            slug
            firstName
            lastName
            photoUrl
            pai
            combine
            highSchools
          }
          userId
          createdAt
          updatedAt
        }
      `)
      .then((data: any) => data.getSavedPlayer)
      .then((data: SavedPlayer) => setSavedPlayer(data))
      .catch(console.error);
  }

  function checkPlayerHasAlert(id: number) {
    return gql(`
        hasAlert (playerId: ${id})
      `)
      .then((data: any) => data.hasAlert as boolean)
      .then((hasAlert: boolean) => setHasAlert(hasAlert))
      .catch(console.error)
  }

  function onToggleArchivePlayer() {
    setArchivePlayerDialogOpen(false);

    if (!player || !player.slug) return;

    setLoading(true);

    gql(`
        mutation {
          toggleArchive(slug: "${player.slug}")
        }
      `)
      .then((data: any) => data.toggleArchive as boolean)
      .then((success: boolean) => {
        if (success) {
          showToast(player.isArchived ? 'Unarchived' : 'Archived', ToastType.SUCCESS);

          setIsArchived(!isArchived);
        } else {
          showToast('Oops, something is wrong. Try again or contact our support team.', ToastType.ERROR);
        }
      })
      .catch(error => {
        console.error(error);
        showToast('Oops, something is wrong. Try again or contact our support team.', ToastType.ERROR);
      })
      .finally(() => setLoading(false));
  }

  function onRemovePlayer() {
    setRemovePlayerDialogOpen(false);

    if (!player || !player.slug) return;

    setLoading(true);

    gql(`
        mutation {
          deletePlayer(slug: "${player.slug}")
        }
      `)
      .then((data: any) => data.deletePlayer as boolean)
      .then((success: boolean) => {
        if (success) {
          showToast('Removed', ToastType.SUCCESS);

          return new Promise<void>((resolve) => {
            setTimeout(() => {
              history.replace('/dashboard');
              resolve();
            }, 2000);
          });
        } else {
          showToast('Oops, something is wrong. Try again or contact our support team.', ToastType.ERROR);
        }
      })
      .catch(error => {
        console.error(error);
        showToast('Oops, something is wrong. Try again or contact our support team.', ToastType.ERROR);
      })
      .finally(() => setLoading(false));
  }

  function pinPlayer() {
    setLoading(true);

    gql(`
        mutation {
          pin (playerIds: [${player && player.id}])
        }
      `)
      .then((data: any) => ({
        success: data.pin as boolean,
        errorMessage: data.errorMessage,
      }))
      .then(({ success, errorMessage }: { success: boolean, errorMessage: string }) => {
        setIsPinned(success);

        if (errorMessage) {
          showToast(errorMessage, ToastType.ERROR);
        }
      })
      .catch(error => {
        console.error(error);
        showToast('Oops, something is wrong. Try again or contact our support team.', ToastType.ERROR);
      })
      .finally(() => setLoading(false));
  }

  function unpinPlayer() {
    setLoading(true);

    gql(`
        mutation {
          unpin (playerIds: [${player && player.id}])
        }
      `)
      .then((data: any) => data && data.unpin as boolean)
      .then((success: boolean) => setIsPinned(!success))
      .catch(error => {
        console.error(error);
        showToast('Oops, something is wrong. Try again or contact our support team.', ToastType.ERROR);
      })
      .finally(() => setLoading(false));
  }

  function savePlayer() {
    setLoading(true);

    gql(`
        mutation {
          savePlayers (playerIds: [${player && player.id}], folderId: null)
        }
      `)
      .then((data: any) => data.savePlayers as boolean)
      .then((isSaved: boolean) => {
        if (isSaved) {
          player && player.id && checkIsSavedPlayer(player.id)
        }
      })
      .catch(error => {
        console.error(error);
        showToast('Oops, something is wrong. Try again or contact our support team.', ToastType.ERROR);
      })
      .finally(() => setLoading(false));
  }

  function unsavePlayer() {
    setLoading(true);

    gql(`
        mutation {
          unsavePlayers (savedPlayerIds: [${savedPlayer && savedPlayer.id}])
        }
      `)
      .then((data: any) => data.unsavePlayers as boolean)
      .then((isSaved: boolean) => {
        if (isSaved) {
          player && player.id && checkIsSavedPlayer(player.id)
        }
      })
      .catch(error => {
        console.error(error);
        showToast('Oops, something is wrong. Try again or contact our support team.', ToastType.ERROR);
      })
      .finally(() => setLoading(false));
  }

  function addAlert() {
    setLoading(true);

    gql(`
        mutation {
          addAlerts (playerIds: [${player && player.id}])
        }
      `)
      .then((data: any) => data.addAlerts as boolean)
      .then((alertStatus: boolean) => setHasAlert(alertStatus))
      .catch((error) => {
        console.error(error);
        showToast('Oops, something is wrong. Try again or contact our support team.', ToastType.ERROR);
      })
      .finally(() => setLoading(false));
  }

  function removeAlert() {
    setLoading(true);

    gql(`
        mutation {
          removeAlerts (playerIds: [${player && player.id}])
        }
      `)
      .then((data: any) => data.removeAlerts as boolean)
      .then((alertRemoved: boolean) => setHasAlert(!alertRemoved))
      .catch((error) => {
        console.error(error);
        showToast('Oops, something is wrong. Try again or contact our support team.', ToastType.ERROR);
      })
      .finally(() => setLoading(false));
  }

  function generateAndDownloadPDFReport() {
    if (!player?.slug) return;

    setLoading(true);

    let exportQueryParameters: any = {
      [PlayerPDFQueryParameter.PROFILE_TYPE]: isNFL ? 'nfl' : 'college',
    };
    if (selectedCollegeTeam?.id) {
      exportQueryParameters[PlayerPDFQueryParameter.SELECTED_COLLEGE_TEAM_ID] = selectedCollegeTeam.id;
    }
    if (isNFL && selectedNFLTeam?.id) {
      exportQueryParameters[PlayerPDFQueryParameter.SELECTED_NFL_TEAM_ID] = selectedNFLTeam.id;
    }
    if (selectedPosition) {
      exportQueryParameters[PlayerPDFQueryParameter.SELECTED_POSITION] = selectedPosition;
    }
    if (selectedTeamType) {
      let selectedTeamForQueryParameters = 'high-school';
      if (selectedTeamType === TEAM_TYPE.COLLEGE) {
        selectedTeamForQueryParameters = 'college';
      } else if (selectedTeamType === TEAM_TYPE.NFL) {
        selectedTeamForQueryParameters = 'nfl';
      }
      exportQueryParameters[PlayerPDFQueryParameter.SELECTED_TEAM_TYPE] = selectedTeamForQueryParameters;
    }

    exportPDFReport(
      PDFExportPage.PLAYER,
      player.slug,
      exportQueryParameters,
    )
      .catch(error => {
        console.error(error);
        showToast(<>Failed to download PDF report. <br />({error.message})</>, ToastType.ERROR);
      })
      .finally(() => setLoading(false));
  }

  function copyToClipboard() {
    navigator.clipboard.writeText(window.location.href);
    showToast('Copied to clipboard', ToastType.INFO);
  }

  function formatValueInK(value?: number | null): string {
    if (value === null || value === undefined) return 'N/A';
    const abs = Math.abs(value);

    if (abs >= 1000) {
      return `${(value / 1000).toFixed(1)}K`;
    }

    return value.toLocaleString();
  }

  const primaryHighSchool = getPrimaryHighSchool(player);
  const playerMultiSport = player?.multiSport?.length ?? 0

  return (
    <>
      <header className={classes.header}>
        <Loader inProgress={loading} />

        <div className={classes.headerContent}>
          {player && (
            <>
              <div className={classes.playerIntro}>
                <div className={classes.avatarContainer}>
                  <Avatar
                    className={classes.avatar}
                    src={player.photoUrl}
                    initials={`${(player.firstName || ' ')[0]}${(player.lastName || ' ')[0]}`.trim()}
                    alt='Profile picture'
                  />
                  {isAdmin && (
                    <EditButton
                      className={classes.editAction}
                      onClick={onEditPlayer}
                    />
                  )}
                </div>

                <div className={classes.playerIntroContent}>
                  <h1 className={classes.playerName}>
                    {player.firstName} {player.lastName}
                  </h1>

                  {primaryHighSchool?.state && (
                    <CFCLogo
                      className={classes.cfcLogo}
                      state={primaryHighSchool.state}
                    />
                  )}
                </div>
              </div>
              
              <div className={classes.playerOverview}>
                <div className={classes.playerOverviewRow}>
                  <div className={classes.scoreAndMultiSportRow}>
                    <div className={classes.playerScoreRow}>
                      <div className={clsx(classes.playerScoreWrapper, !!player?.paiHasFreshmanData ? classes.leftAlign : '')}>
                        <LabledScore
                          className={classes.playerScore}
                          noScoreTextClass={classes.noScoreText}
                          type={SCORE_TYPE.PAI}
                          scoreList={player.pai}
                          position={selectedPosition}
                          label='PAI'
                        />

                        {/* {!!player?.paiHasFreshmanData && (
                          <Tooltip title='Freshman Data'>
                            <FreshmanIcon className={clsx(classes.freshmanIcon, classes.paiFreshmanIcon)} />
                          </Tooltip>
                        )} */}
                      </div>

                      <div className={clsx(classes.playerScoreWrapper, !!player?.combineHasFreshmanData ? classes.leftAlign : '')}>
                        <LabledScore
                          className={classes.playerScore}
                          noScoreTextClass={classes.noScoreText}
                          checkIconClass={classes.checkIconClass}
                          type={SCORE_TYPE.COMBINE}
                          scoreList={player.combine}
                          position={selectedPosition}
                          combines={player.hsCombines}
                          label='COM'
                        />

                        {/* {!!player?.combineHasFreshmanData && (
                          <Tooltip title='Freshman Data'>
                            <FreshmanIcon className={clsx(classes.freshmanIcon, classes.combineFreshmanIcon)} />
                          </Tooltip>
                        )} */}
                      </div>

                      {player?.ppi && (user?.accessLevel?.hasCollegeAccess || user?.accessLevel?.hasNFLAccess) && (
                        <div className={clsx(classes.playerScoreWrapper, classes.proCombineWrapper)}>
                          <LabledScore
                            className={classes.playerScore}
                            noScoreTextClass={classes.noScoreText}
                            checkIconClass={classes.checkIconClass}
                            type={SCORE_TYPE.PPI}
                            value={player.ppi || undefined}
                            position={selectedPosition}
                            label='PPI'
                          />
                        </div>
                      )}

                      {
                        IS_ADMIN_OR_NFL_SCOUT
                          && selectedTeamType === TEAM_TYPE.COLLEGE
                          && player?.bestCollegeMPH?.mph ? <div className={clsx(classes.playerScoreWrapper, !!player?.combineHasFreshmanData ? classes.leftAlign : '')}>
                          <LabledScore
                            className={classes.playerScore}
                            noScoreTextClass={classes.noScoreText}
                            checkIconClass={classes.checkIconClass}
                            value={player?.bestCollegeMPH?.mph}
                            type={SCORE_TYPE.MPH}
                            totalValue={player?.bestCollegeMPH?.mph || 0}
                            label='MPH'
                          />
                        </div> : ''}

                        {selectedTeamType === TEAM_TYPE.HIGH_SCHOOL && player?.bestHighSchoolMPH?.mph && (user?.accessLevel?.hasCollegeAccess || user?.accessLevel?.hasNFLAccess) && (
                          <div className={clsx(classes.playerScoreWrapper)}>
                            <LabledScore
                              className={classes.playerScore}
                              noScoreTextClass={classes.noScoreText}
                              checkIconClass={classes.checkIconClass}
                              type={SCORE_TYPE.MPH}
                              value={(selectedTeamType === TEAM_TYPE.HIGH_SCHOOL ? player?.bestHighSchoolMPH?.mph : player?.bestCollegeMPH?.mph) || undefined}
                              totalValue={(selectedTeamType === TEAM_TYPE.HIGH_SCHOOL ? player?.bestHighSchoolMPH?.mph : player?.bestCollegeMPH?.mph) || undefined}
                              label='MPH'
                            />
                          </div>
                        )}

                        {(player?.nflCombineScore || !!player?.nflCombines?.length) && (
                          <div className={clsx(classes.playerScoreWrapper, classes.proCombineWrapper)}>
                            <LabledScore
                              className={classes.playerScore}
                              checkIconClass={classes.checkIconClass}
                              type={SCORE_TYPE.PRO_COMBINE}
                              value={player.nflCombineScore || undefined}
                              position={selectedPosition}
                              combines={player.nflCombines}
                              label='Pro Com'
                            />
                          </div>
                        )}

                      {/* {selectedTeamType === TEAM_TYPE.HIGH_SCHOOL && player?.bestHighSchoolMPH?.mph && (user?.accessLevel?.hasCollegeAccess || user?.accessLevel?.hasNFLAccess) && (
                        <div className={clsx(classes.playerScoreWrapper)}>
                          <LabledScore
                            className={classes.playerScore}
                            noScoreTextClass={classes.noScoreText}
                            checkIconClass={classes.checkIconClass}
                            type={SCORE_TYPE.MPH}
                            value={(selectedTeamType === TEAM_TYPE.HIGH_SCHOOL ? player?.bestHighSchoolMPH?.mph : player?.bestCollegeMPH?.mph) || undefined}
                            totalValue={(selectedTeamType === TEAM_TYPE.HIGH_SCHOOL ? player?.bestHighSchoolMPH?.mph : player?.bestCollegeMPH?.mph) || undefined}
                            label='MPH'
                          />
                        </div>
                      )}

                      {player?.ppi && (user?.accessLevel?.hasCollegeAccess || user?.accessLevel?.hasNFLAccess) && (
                        <div className={clsx(classes.playerScoreWrapper, classes.proCombineWrapper)}>
                          <LabledScore
                            className={classes.playerScore}
                            noScoreTextClass={classes.noScoreText}
                            checkIconClass={classes.checkIconClass}
                            type={SCORE_TYPE.PPI}
                            value={player.ppi || undefined}
                            position={selectedPosition}
                            label='PPI'
                          />
                        </div>
                      )}

                      {(player?.nflCombineScore || !!player?.nflCombines?.length) && (
                        <div className={clsx(classes.playerScoreWrapper, classes.proCombineWrapper)}>
                          <LabledScore
                            className={classes.playerScore}
                            checkIconClass={classes.checkIconClass}
                            type={SCORE_TYPE.PRO_COMBINE}
                            value={player.nflCombineScore || undefined}
                            position={selectedPosition}
                            combines={player.nflCombines}
                            label='Pro Com'
                          />
                        </div>
                      )} */}
                    </div>
                    {/* <div>

                    </div> */}
                    <div className={classes.playerMultiSColumn}>
                      {/* <div className={classes.multiSportSpecialistChip}>
                        Specialist
                      </div> */}

                      <div className={classes.playerMultiSSection}>
                        <span className={classes.multiSportCount}>
                          <Tooltip
                            title={playerMultiSport > 0 ? (
                              <MultiSportList
                                className={classes.multiSportTooltipList}
                                iconClassName={classes.multiSportTooltipIcon}
                                list={player?.multiSport}
                              />
                            ) : 'Specialist' }
                          >
                            <div>{playerMultiSport}</div>
                          </Tooltip>
                        </span>

                        <div className={classes.multiSportLabel}>
                          HS Sports
                        </div>
                      </div>

                      <div className={classes.playerMultiSSection}>
                        <span className={classes.multiSportCount}>
                          N/A
                        </span>

                        <div className={classes.multiSportLabel}>
                          <TFPillLogoColorIcon className={classes.multiSportIcon} />
                        </div>
                      </div>

                      <div className={classes.playerMultiSSection}>
                        <span className={classes.multiSportCount}>
                          {formatValueInK(player?.on3PublicNILValuation)}
                        </span>

                        <div className={classes.multiSportLabel}>
                          <On3LogoIcon className={classes.multiSportIcon} />
                        </div>
                      </div>
                    </div>
                  </div>

                  {(user?.accessLevel?.canTeamSelectCollege || user?.accessLevel?.canTeamSelectNFL) && (
                    <div className={classes.teamDropDownWrapper}>
                      {selectedCollegeTeam && (
                        <>
                          <span className={classes.teamDropDownLabel}>TeamSelect:</span>

                          {user?.accessLevel?.canTeamSelectCollege && (
                            <TeamAutoComplete
                              className={clsx(classes.teamSelect, isNFL && classes.withNFLTeamSelect)}
                              teams={divITeams}
                              selectedTeam={selectedCollegeTeam}
                              onSelect={onCollegeTeamSelected}
                            />
                          )}

                          {(isNFL && user?.accessLevel?.canTeamSelectNFL) && (
                            <TeamAutoComplete
                              className={clsx(classes.teamSelect, classes.withNFLTeamSelect)}
                              teams={nflTeams}
                              selectedTeam={selectedNFLTeam}
                              onSelect={onNFLTeamSelected}
                            />
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>

                <div className={classes.playerOverviewRow}>
                  <div className={classes.toolbarRow}>
                    <Toggle
                      className={classes.switch}
                      items={(positions ?? []).map((position: Position) => ({
                        value: position,
                        content: position,
                      }))}
                      value={String(selectedPosition)}
                      onSelect={(value: string) => setSelectedPosition(value as Position)}
                    />

                    <div className={classes.actions}>
                      {isAdmin && (
                        <IconButton
                          className={classes.action}
                          icon={RemoveIcon}
                          iconClassName={classes.actionIcon}
                          onClick={() => setArchivePlayerDialogOpen(true)}
                        />
                      )}

                      {isAdmin && (
                        <IconButton
                          className={classes.action}
                          icon={RemoveCircleOutlineOutlined}
                          iconClassName={classes.actionIcon}
                          onClick={() => setRemovePlayerDialogOpen(true)}
                        />
                      )}

                      <IconButton
                        className={classes.action}
                        icon={LinkIcon}
                        iconClassName={classes.actionIcon}
                        onClick={copyToClipboard}
                      />

                      <IconButton
                        className={classes.action}
                        icon={PinIcon}
                        iconClassName={classes.actionIcon}
                        onClick={isPinned ? unpinPlayer : pinPlayer}
                      />

                      <IconButton
                        className={classes.action}
                        icon={PDFIcon}
                        iconClassName={classes.actionIcon}
                        onClick={generateAndDownloadPDFReport}
                        disabled={loading}
                      />

                      {user?.accessLevel?.canComparePlayers && (
                        <IconButton
                          className={classes.action}
                          icon={CompareIcon}
                          iconClassName={classes.actionIcon}
                          href={player && player.slug ? `/compare/${player.slug}` : '/compare'}
                        />
                      )}

                      <IconButton
                        className={classes.action}
                        icon={SaveIcon}
                        iconClassName={classes.actionIcon}
                        onClick={savedPlayer !== null ? unsavePlayer : savePlayer}
                      />

                      {!isNFL && !(playerHasColleges || playerHasNFLTeams) && (
                        <IconButton
                          className={classes.action}
                          icon={AlertIcon}
                          iconClassName={classes.actionIcon}
                          onClick={hasAlert ? removeAlert : addAlert}
                        />
                      )}
                    </div>
                  </div>
                </div>
              </div>

            </>
          )}
        </div>
      </header>
      <ConfirmDialog
        open={archivePlayerDialogOpen}
        confirmActionText={`Yes, ${isArchived ? 'Unarchive' : 'Archive'}`}
        onConfirm={onToggleArchivePlayer}
        onClose={() => setArchivePlayerDialogOpen(false)}
        onCancel={() => setArchivePlayerDialogOpen(false)}
      />

      <ConfirmDialog
        open={removePlayerDialogOpen}
        confirmActionText={`Yes, Remove`}
        onConfirm={onRemovePlayer}
        onClose={() => setRemovePlayerDialogOpen(false)}
        onCancel={() => setRemovePlayerDialogOpen(false)}
      />
    </>
  )
}
